function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useState } from 'react';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { ContentPage } from 'common/content/page';
import { HeatMap, HeatMapPageStatus } from 'common/widgets/heat-maps/types';
import { createHeatMap, deleteHeatMap, getHeatMap, updateHeatMap } from 'common/report/queries/app-api/heat-maps-client';
import { reporter } from 'common/report/error-reporting';
import { canEditEntity, normalizeHeatMapEntity } from 'common/report/util/saved-entities';
import { EntityType } from 'common/report/util/saved-entities/types';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { useTranslation } from 'common/i18n/hooks';
import { ToastType } from 'common/components/toast/types';
import { addToast } from 'common/components/toast-area/toast-manager';
import { confirm } from 'common/components/confirm';
import { THEME as ConfirmTheme } from 'common/components/confirm/types';
import { navigate } from 'common/util/url';
import { getReportUrl } from 'common/report/util';
import { ContentActionType, ContentType, Dashboard } from 'common/report/dashboards/types';
import { dashboardsClient } from 'common/report/queries/app-api/dashboards-client';
import { trimHeatMapForAPI } from 'apps/heat-maps/src/util';
import { HeatMapPageHeader } from '../header';
import { HeatMapPageMain } from '../main';
type HeatMapPageProps = {
  projectId: number;
  workspaceId: number;
  userId: number;
  heatMapId: string;
  sessionReplayUrl?: string;
};
export function HeatMapPage(_ref: HeatMapPageProps) {
  let {
    projectId,
    workspaceId,
    userId,
    heatMapId,
    sessionReplayUrl
  }: HeatMapPageProps = _ref;
  const [currentHeatMap, setCurrentHeatMap] = useState<HeatMap | null>(heatMapId ? null : {
    title: "",
    url: sessionReplayUrl !== null && sessionReplayUrl !== void 0 ? sessionReplayUrl : ""
  } as HeatMap);
  const [currentHeatMapDashboard, setCurrentHeatMapDashboard] = useState<Dashboard>();
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = React.useState<HeatMapPageStatus>("success");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const currentProject = useCurrentProject();
  const {
    t
  } = useTranslation();
  const getCurrentHeatMap = async () => {
    setStatus("loading");
    try {
      const heatMapFound = await getHeatMap(projectId, heatMapId!);
      setCurrentHeatMap(heatMapFound);
      if (heatMapFound.dashboard_id) {
        const dashboardFound = await dashboardsClient.querySingle({
          projectId,
          workspaceId,
          dashboardID: heatMapFound.dashboard_id
        }).network;
        setCurrentHeatMapDashboard(dashboardFound);
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMessage(err.message);
      reporter.error(err, "getHeatMap error", {
        projectId,
        heatMapId
      });
    }
  };
  useEffect(() => {
    if (heatMapId && heatMapId !== (currentHeatMap === null || currentHeatMap === void 0 ? void 0 : currentHeatMap.id)) {
      getCurrentHeatMap();
    }
  }, [projectId, workspaceId, heatMapId]);
  async function onEditHeatMap(newHeatMap: HeatMap) {
    if (newHeatMap.id) {
      setSaving(true);
      const existingHeatMap: HeatMap | null = currentHeatMap ? _objectSpread({}, currentHeatMap as HeatMap) : null;
      setCurrentHeatMap(newHeatMap);
      const trimmedHeatMap = trimHeatMapForAPI(newHeatMap);
      try {
        const updatedHeatMap = await updateHeatMap(currentProject.id, trimmedHeatMap as HeatMap);
        setCurrentHeatMap(updatedHeatMap);
      } catch (error) {
        setCurrentHeatMap(existingHeatMap);
        reporter.error(error, "updateHeatMap error", {
          projectId: currentProject.id,
          heatMap: newHeatMap
        });
        addToast({
          message: t("heat-maps.update-api-error"),
          type: ToastType.ERROR
        });
      }
      setSaving(false);
    } else {
      setCurrentHeatMap(newHeatMap);
    }
  }
  async function onDuplicateHeatMap(originalHeatMap: HeatMap): Promise<HeatMap | null> {
    const newHeatmap = _objectSpread(_objectSpread({}, trimHeatMapForAPI(originalHeatMap)), {}, {
      title: "Copy of ".concat(originalHeatMap.title)
    });
    delete newHeatmap.id;
    delete newHeatmap.dashboard_id;
    try {
      const createdHeatMap = await createHeatMap(currentProject.id, newHeatmap as HeatMap);
      window.open(getReportUrl("heatmaps/".concat(createdHeatMap.id), {
        projectId,
        workspaceId
      }), "_blank");
      return createdHeatMap;
    } catch (error) {
      reporter.error(error, "createHeatMap error", {
        projectId: currentProject.id
      });
      addToast({
        message: t("heat-maps.create-api-error"),
        type: ToastType.ERROR
      });
      return null;
    }
  }
  async function onDeleteHeatMap(heatMap: HeatMap) {
    if (await confirm({
      title: t("heat-maps.delete-heat-map-title"),
      description: t("heat-maps.delete-heat-map-text", {
        heatMapName: heatMap.title
      }),
      theme: ConfirmTheme.DELETE,
      'confirm-button-label': t("common.delete"),
      'description-allow-newline': true
    })) {
      try {
        if (currentHeatMapDashboard) {
          const fields = {
            content: {
              action: ContentActionType.Delete,
              content_id: heatMap.id,
              content_type: ContentType.Heatmap
            }
          };
          dashboardsClient.updateDashboardFields({
            projectId,
            dashboardID: currentHeatMapDashboard.id,
            fields
          });
          navigate("".concat(getReportUrl("boards", {
            projectId,
            workspaceId
          }), "#id=").concat(currentHeatMapDashboard.id));
        } else {
          deleteHeatMap(currentProject.id, heatMap.id);
          navigate(getReportUrl("home", {
            projectId,
            workspaceId
          }));
        }
      } catch (error) {
        reporter.error(error, "deleteHeatMap error", {
          projectId: currentProject.id,
          heatMapId: heatMap.id
        });
        addToast({
          message: t("heat-maps.delete-api-error"),
          type: ToastType.ERROR
        });
      }
    }
  }
  async function onSaveNewHeatMap(heatMap: HeatMap, dashboardId: number, createNew: boolean, shouldDuplicate: boolean) {
    if (createNew) {
      var _response$new_content;
      setStatus("loading");
      const fields = {
        content: {
          action: ContentActionType.Create,
          content_type: ContentType.Heatmap,
          content_params: _objectSpread(_objectSpread({}, trimHeatMapForAPI(heatMap)), {}, {
            id: undefined,
            dashboard_id: undefined
          })
        }
      };
      const response = await dashboardsClient.updateDashboardFields({
        projectId,
        dashboardID: dashboardId,
        fields
      });
      navigate(getReportUrl("heatmaps/".concat((_response$new_content = response.new_content) === null || _response$new_content === void 0 ? void 0 : _response$new_content.id), {
        projectId,
        workspaceId
      }));
      setStatus("success");
    } else {
      if (shouldDuplicate) {
        var _response$new_content2;
        setStatus("loading");
        const fields = {
          content: {
            action: ContentActionType.Duplicate,
            content_id: heatMap.id,
            content_type: ContentType.Heatmap
          }
        };
        const response = await dashboardsClient.updateDashboardFields({
          projectId,
          dashboardID: dashboardId,
          fields
        });
        navigate(getReportUrl("heatmaps/".concat((_response$new_content2 = response.new_content) === null || _response$new_content2 === void 0 ? void 0 : _response$new_content2.id), {
          projectId,
          workspaceId
        }));
        setStatus("success");
      } else {
        setSaving(true);
        const fields = {
          content: {
            action: ContentActionType.UnMove,
            content_id: heatMap.id,
            content_type: ContentType.Heatmap
          }
        };
        const response = await dashboardsClient.updateDashboardFields({
          projectId,
          dashboardID: dashboardId,
          fields
        });
        setCurrentHeatMapDashboard(response);
        setCurrentHeatMap(currentHeatMap ? _objectSpread(_objectSpread({}, currentHeatMap as HeatMap), {}, {
          dashboard_id: response.id
        }) : null);
        setSaving(false);
      }
    }
  }
  const heatMapEntity = React.useMemo(() => normalizeHeatMapEntity(currentHeatMap), [currentHeatMap]);
  const canEdit = React.useMemo(() => {
    if (heatMapEntity && !heatMapEntity.id) {
      // If the heat map entity does not have an ID, it means it's a new heat map
      // and we can allow editing.
      return true;
    }
    return heatMapEntity ? canEditEntity({
      entity: heatMapEntity,
      entityType: EntityType.HeatMap,
      projectId
    }) : false;
  }, [heatMapEntity, projectId]);
  if (!heatMapEntity || !currentHeatMap || status === "loading") {
    return <SpinnerReact size={SpinnerSize.Large} />;
  } else if (status === "error") {
    return <div>{errorMessage}</div>;
  }
  return <ContentPage projectId={projectId} workspaceId={workspaceId} userId={userId} header={<HeatMapPageHeader projectId={projectId} workspaceId={workspaceId} userId={userId} heatMap={currentHeatMap} saving={saving} canEdit={canEdit} heatMapEntity={heatMapEntity} heatMapDashboard={currentHeatMapDashboard} onEditHeatMap={onEditHeatMap} onDuplicateHeatMap={onDuplicateHeatMap} onDeleteHeatMap={onDeleteHeatMap} onSaveNewHeatMap={onSaveNewHeatMap} />} data-sentry-element="ContentPage" data-sentry-component="HeatMapPage" data-sentry-source-file="index.tsx">
      <HeatMapPageMain heatMap={currentHeatMap} heatMapEntity={heatMapEntity} onEditHeatMap={onEditHeatMap} canEdit={canEdit} data-sentry-element="HeatMapPageMain" data-sentry-source-file="index.tsx" />
    </ContentPage>;
}